import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  TablePagination,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import palette from 'src/theme/palette';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { getLastNDaysDateRange } from 'src/utils/dateUtils';
import {
  getLookbackPlanLimitCaption,
  getLookbackPresetBlockedMessage,
} from 'src/utils/lookbackMessages';
import { GET_FREQUENTLY_BOUGHT_TOGETHER } from 'src/utils/gql/inventoryQueries';

const BUNDLES_REQUEST_LIMIT = 200;
const ROWS_PER_PAGE_OPTIONS = [12, 24, 48];
type SortBy = 'orders' | 'affinity';
const PDF_PAGE_WIDTH_PX = 820;
const PDF_PAGE_HEIGHT_PX = Math.round((PDF_PAGE_WIDTH_PX * 297) / 210); // A4 portrait ratio
// 2 colonne → 4 righe per pagina (8 item) evita overflow verticale (che poi viene scalato e “schiacciato” nel PDF)
const PDF_ITEMS_PER_PAGE = 6;

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    maxWidth: 'calc(100% - 240px)',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    marginTop: '60px',
    maxWidth: '100%',
  },
}));

const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: 16,
  paddingBottom: 24,
  boxSizing: 'border-box',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const BundleCard = styled(Card)(() => ({
  background: '#ffffff',
  border: '1px solid #eef2f7',
  borderRadius: 12,
  padding: '16px 16px 8px 16px', // bottom 8px sotto indicatore affinity
  boxShadow: '0 4px 12px rgba(2,6,23,.08)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(2,6,23,.12)',
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const BundleRank = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
  color: 'white',
  fontWeight: 700,
  fontSize: 12,
  padding: '4px 8px',
  borderRadius: 6,
  display: 'inline-block',
}));

const BundleStats = styled(Box)(() => ({
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
}));

const StatChip = styled(Box)<{ variant?: 'orders' | 'confidence' }>(
  ({ variant = 'orders' }) => ({
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 8px',
    borderRadius: 4,
    ...(variant === 'orders'
      ? { background: '#f0f9ff', color: '#0369a1' }
      : { background: '#f0fdf4', color: '#166534' }),
  }),
);

const ProductRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  marginBottom: 8,
}));

const ProductBlock = styled(Box)(() => ({
  flex: 1,
  textAlign: 'center',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

// Contenitore quadrato forzato (padding-bottom % = width) così flex/grid non stira l’immagine
const ProductImage = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  aspectRatio: '1',
  position: 'relative',
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: '#f3f4f6',
  marginBottom: 6,
  flexShrink: 0,
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    // In pagina le immagini “riempiono” il riquadro (con crop), non devono risultare più piccole nel PDF.
    // UI: contain mostra l'immagine intera, niente taglio
    objectFit: 'contain',
    objectPosition: 'center',
  },
}));

// PDF: box quadrato forzato (height da padding) così l'altezza non viene schiacciata; contain senza object-fit.
const PdfProductImage = styled(Box)(() => ({
  width: '100%',
  position: 'relative',
  height: 0,
  paddingBottom: '100%',
  boxSizing: 'content-box',
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: '#f3f4f6',
  marginBottom: 6,
  flexShrink: 0,
  '& img': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
  },
}));

const ProductName = styled(Typography)(() => ({
  fontSize: 12,
  fontWeight: 600,
  color: '#111827',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const ProductColor = styled(Typography)(() => ({
  fontSize: 10,
  color: '#6b7280',
  marginTop: 2,
}));

const PlusBadge = styled(Box)(() => ({
  background: '#f3f4f6',
  color: '#6b7280',
  fontWeight: 700,
  fontSize: 14,
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const TrendBar = styled(Box)(() => ({
  marginTop: 8,
  height: 6,
  borderRadius: 3,
  backgroundColor: '#e5e7eb',
  overflow: 'hidden',
}));

const TrendFill = styled(Box)<{ width: number }>(({ width }) => ({
  height: '100%',
  width: `${Math.min(100, width)}%`,
  background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
  borderRadius: 3,
}));

export interface BundleItem {
  p1_id: string;
  p2_id: string;
  p1_title: string;
  p2_title: string;
  p1_image: string;
  p2_image: string;
  together_orders: number;
  confidence: number;
  affinity: number;
}

function parseProductTitle(title: string): { name: string; color: string } {
  const inIndex = title.indexOf(' IN ');
  if (inIndex > 0) {
    return {
      name: title.slice(0, inIndex).trim(),
      color: title.slice(inIndex + 4).trim(),
    };
  }
  return { name: title, color: '' };
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  if (size <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const Bundles: React.FC = () => {
  const snackbar = useSnackbar();
  const { lookbackDays } = useTrialStatus();
  const [dateRangePreset, setDateRangePreset] = useState('30');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('orders');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const pdfPageRefs = useRef<Array<HTMLDivElement | null>>([]);

  const dateRange = useMemo(() => {
    if (showCustomDateRange && tempCustomDates?.from && tempCustomDates?.to) {
      return {
        from: tempCustomDates.from.includes('T')
          ? tempCustomDates.from
          : `${tempCustomDates.from}T00:00:00.000Z`,
        to: tempCustomDates.to.includes('T')
          ? tempCustomDates.to
          : `${tempCustomDates.to}T23:59:59.999Z`,
      };
    }
    const days = parseInt(dateRangePreset, 10) || 30;
    const range = getLastNDaysDateRange(days);
    return {
      from: `${range.from}T00:00:00.000Z`,
      to: `${range.to}T23:59:59.999Z`,
    };
  }, [dateRangePreset, showCustomDateRange, tempCustomDates]);

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const canUsePresetDays = useCallback(
    (days: number) => {
      if (lookbackDays === null || lookbackDays === undefined) return true;
      return days <= lookbackDays;
    },
    [lookbackDays],
  );

  const { data, loading, error } = useQuery<{
    getFrequentlyBoughtTogether: BundleItem[];
  }>(GET_FREQUENTLY_BOUGHT_TOGETHER, {
    variables: {
      dateRange: { from: dateRange.from, to: dateRange.to },
      limit: BUNDLES_REQUEST_LIMIT,
    },
    fetchPolicy: 'cache-and-network',
  });

  const rawBundles = data?.getFrequentlyBoughtTogether ?? [];

  const sortedBundles = useMemo(() => {
    const list = [...rawBundles];
    if (sortBy === 'orders') {
      list.sort(
        (a, b) =>
          b.together_orders - a.together_orders || b.affinity - a.affinity,
      );
    } else {
      list.sort(
        (a, b) =>
          b.affinity - a.affinity || b.together_orders - a.together_orders,
      );
    }
    return list;
  }, [rawBundles, sortBy]);

  const pdfPages = useMemo(() => {
    return chunkArray(sortedBundles, PDF_ITEMS_PER_PAGE);
  }, [sortedBundles]);

  useEffect(() => {
    // Mantieni la stessa lunghezza (evita ref "fantasma" tra render)
    pdfPageRefs.current = pdfPageRefs.current.slice(0, pdfPages.length);
  }, [pdfPages.length]);

  const paginatedBundles = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedBundles.slice(start, start + rowsPerPage);
  }, [sortedBundles, page, rowsPerPage]);

  const totalCount = sortedBundles.length;
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const waitForImages = useCallback((element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
      const images = element.querySelectorAll('img');
      if (images.length === 0) return resolve();
      let loaded = 0;
      const total = images.length;
      const done = () => {
        loaded += 1;
        if (loaded >= total) resolve();
      };
      images.forEach((img) => {
        if (img.complete) done();
        else {
          img.onload = done;
          img.onerror = done;
        }
      });
    });
  }, []);

  const ensureHtml2PdfLoaded = useCallback(async (): Promise<void> => {
    if ((window as any).html2pdf) return;
    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-kipify-html2pdf="1"]',
      ) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener(
          'error',
          () => reject(new Error('Failed to load html2pdf')),
          {
            once: true,
          },
        );
        return;
      }
      const script = document.createElement('script');
      script.dataset.kipifyHtml2pdf = '1';
      script.src =
        'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load html2pdf'));
      document.head.appendChild(script);
    });
  }, []);

  // html2pdf.bundle non espone html2canvas/jspdf su window: carichiamoli esplicitamente per PDF multi-pagina
  const loadScript = useCallback(
    (src: string, dataAttr: string): Promise<void> => {
      const attr = `data-kipify-${dataAttr}`;
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[${attr}="1"]`)) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.setAttribute(attr, '1');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load ${dataAttr}`));
        document.head.appendChild(script);
      });
    },
    [],
  );

  const ensurePdfEngineLoaded = useCallback(async (): Promise<void> => {
    await ensureHtml2PdfLoaded();
    const win = window as any;
    if (!win.html2canvas) {
      await loadScript(
        'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
        'html2canvas',
      );
    }
    if (!win.jspdf?.jsPDF) {
      await loadScript(
        'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
        'jspdf',
      );
    }
  }, [ensureHtml2PdfLoaded, loadScript]);

  const downloadAsPDF = useCallback(async () => {
    try {
      setIsDownloadingPdf(true);
      setShowReportPreview(true); // monta il contenuto in DOM (overlay nascosta sotto con opacity 0)
      await new Promise((r) =>
        requestAnimationFrame(() => requestAnimationFrame(r)),
      );
      await new Promise((r) => setTimeout(r, 400)); // tempo per layout e paint

      await ensurePdfEngineLoaded();

      const win = window as any;
      const html2canvas = win.html2canvas as
        | ((el: HTMLElement, options?: any) => Promise<HTMLCanvasElement>)
        | undefined;
      const JsPDF = (win.jspdf?.jsPDF ?? win.jsPDF) as
        | (new (opts: any) => {
            internal: {
              pageSize: { getWidth: () => number; getHeight: () => number };
            };
            addPage: () => void;
            addImage: (
              imgData: string,
              format: string,
              x: number,
              y: number,
              w: number,
              h: number,
              alias?: string,
              compression?: string,
            ) => void;
            save: (filename: string) => void;
          })
        | undefined;

      if (!html2canvas || !JsPDF) {
        console.error(
          '[Bundles PDF] html2canvas present:',
          !!html2canvas,
          'jspdf present:',
          !!win.jspdf,
          'jsPDF:',
          !!JsPDF,
        );
        snackbar(
          'PDF: librerie non caricate. Ricarica la pagina e riprova.',
          'error',
        );
        return;
      }

      const pdf = new JsPDF({
        unit: 'pt',
        format: 'a4',
        orientation: 'portrait',
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pdfPageRefs.current.length; i += 1) {
        const pageEl = pdfPageRefs.current[i];
        if (!pageEl) {
          console.warn('[Bundles PDF] page ref missing for index', i);
          continue;
        }

        pageEl.scrollIntoView({ block: 'start' });
        await new Promise((r) =>
          requestAnimationFrame(() => requestAnimationFrame(r)),
        );
        await new Promise((r) => setTimeout(r, 100));
        await waitForImages(pageEl);

        try {
          const canvas = await html2canvas(pageEl, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: process.env.NODE_ENV === 'development',
            scrollX: 0,
            scrollY: 0,
          });
          const imgData = canvas.toDataURL('image/jpeg', 0.98);
          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, 0, pageW, pageH, undefined, 'FAST');
        } catch (pageErr) {
          const msg =
            pageErr instanceof Error ? pageErr.message : String(pageErr);
          console.error(
            '[Bundles PDF] html2canvas failed for page',
            i + 1,
            msg,
            pageErr,
          );
          throw pageErr;
        }
      }

      pdf.save('kipify-bundles.pdf');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const stack = err instanceof Error ? err.stack : undefined;
      console.error(
        '[Bundles PDF] Error downloading bundles PDF:',
        msg,
        stack ?? err,
      );
      snackbar('Errore durante il download del PDF', 'error');
    } finally {
      setIsDownloadingPdf(false);
      setShowReportPreview(false);
    }
  }, [ensurePdfEngineLoaded, snackbar, waitForImages]);

  return (
    <PageContainer>
      <ContentContainer>
        <Box mb={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{ color: palette.primary.main, fontWeight: 700 }}
            >
              Frequently Bought Together
            </Typography>
            <Button
              variant="contained"
              onClick={downloadAsPDF}
              disabled={loading || totalCount === 0 || isDownloadingPdf}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {isDownloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Product combinations customers buy together. Use this for
            merchandising and cross-sell.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 'fit-content' }}
            >
              Period:
            </Typography>
            <FormControl
              size="small"
              sx={{
                width: { xs: '100%', sm: 180 },
                flex: '0 0 auto',
              }}
            >
              <Select
                value={dateRangePreset}
                onChange={(e) => {
                  const preset = e.target.value;
                  if (preset !== 'custom') {
                    const days = parseInt(preset, 10);
                    if (!isNaN(days) && !canUsePresetDays(days)) {
                      snackbar(
                        getLookbackPresetBlockedMessage(lookbackDays),
                        'warning',
                      );
                      return;
                    }
                  }
                  setDateRangePreset(preset);
                  setPage(0);
                  setShowCustomDateRange(preset === 'custom');
                  if (preset !== 'custom') {
                    setTempCustomDates(null);
                  } else {
                    const range = getLastNDaysDateRange(30);
                    setTempCustomDates({ from: range.from, to: range.to });
                  }
                }}
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.primary.main,
                  },
                }}
              >
                <MenuItem value="7" disabled={!canUsePresetDays(7)}>
                  Last 7 days
                </MenuItem>
                <MenuItem value="30" disabled={!canUsePresetDays(30)}>
                  Last 30 days
                </MenuItem>
                <MenuItem value="90" disabled={!canUsePresetDays(90)}>
                  Last 90 days
                </MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>

            {showCustomDateRange && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={tempCustomDates?.from ?? ''}
                  onChange={(e) =>
                    setTempCustomDates((prev) => ({
                      from: e.target.value,
                      to: prev?.to ?? '',
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: lookbackMinDate || undefined,
                  }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={tempCustomDates?.to ?? ''}
                  onChange={(e) =>
                    setTempCustomDates((prev) => ({
                      from: prev?.from ?? '',
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDates?.from || lookbackMinDate || undefined,
                    max: new Date().toISOString().split('T')[0],
                  }}
                />
              </Box>
            )}
            {lookbackDays !== null && lookbackDays !== undefined && (
              <Typography
                variant="caption"
                sx={{ width: '100%', color: 'warning.main', fontWeight: 500 }}
              >
                {getLookbackPlanLimitCaption(lookbackDays)}
              </Typography>
            )}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 'fit-content', ml: { xs: 0, sm: 2 } }}
            >
              Sort:
            </Typography>
            <FormControl
              size="small"
              sx={{
                width: { xs: '100%', sm: 240 },
                flex: '0 0 auto',
              }}
            >
              <Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value as SortBy);
                  setPage(0);
                }}
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.primary.main,
                  },
                }}
              >
                <MenuItem value="orders">Orders (high first)</MenuItem>
                <MenuItem value="affinity">Affinity (high first)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error.message}
          </Typography>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && (
          <>
            {totalCount === 0 ? (
              <Typography color="text.secondary">
                No product bundles found in the selected period. Try a longer
                range or check that you have orders with multiple products.
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3, // più aria tra paginazione e griglia
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {totalCount} bundle{totalCount !== 1 ? 's' : ''} total
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                      labelRowsPerPage="Rows per page:"
                      showFirstButton
                      showLastButton
                      sx={{
                        // evita layout “lunghi” e stringhe che spingono fuori la UI
                        '& .MuiTablePagination-toolbar': {
                          flexWrap: 'wrap',
                          gap: 1,
                          paddingLeft: 0,
                          paddingRight: 0,
                        },
                        '& .MuiTablePagination-actions': {
                          marginLeft: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(300px, 1fr))',
                    // Evita accavallamento visivo (boxShadow) tra righe
                    columnGap: 4,
                    rowGap: 6,
                  }}
                >
                  {paginatedBundles.map((b, idx) => {
                    const globalIndex = page * rowsPerPage + idx;
                    const p1 = parseProductTitle(b.p1_title);
                    const p2 = parseProductTitle(b.p2_title);
                    const confPct = (b.confidence ?? 0) * 100;
                    return (
                      <BundleCard key={`${b.p1_id}-${b.p2_id}`}>
                        <CardContent
                          sx={{
                            p: 0,
                            '&:last-child': { pb: 0 },
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <BundleRank>#{globalIndex + 1}</BundleRank>
                            <BundleStats>
                              <StatChip variant="orders">
                                {b.together_orders} orders
                              </StatChip>
                              <StatChip variant="confidence">
                                {confPct.toFixed(0)}% confidence
                              </StatChip>
                            </BundleStats>
                          </Box>
                          <ProductRow>
                            <ProductBlock>
                              {b.p1_image ? (
                                <ProductImage>
                                  <img src={b.p1_image} alt={p1.name} />
                                </ProductImage>
                              ) : (
                                <ProductImage />
                              )}
                              <ProductName>{p1.name}</ProductName>
                              {p1.color && (
                                <ProductColor>{p1.color}</ProductColor>
                              )}
                            </ProductBlock>
                            <PlusBadge>+</PlusBadge>
                            <ProductBlock>
                              {b.p2_image ? (
                                <ProductImage>
                                  <img src={b.p2_image} alt={p2.name} />
                                </ProductImage>
                              ) : (
                                <ProductImage />
                              )}
                              <ProductName>{p2.name}</ProductName>
                              {p2.color && (
                                <ProductColor>{p2.color}</ProductColor>
                              )}
                            </ProductBlock>
                          </ProductRow>
                          <TrendBar>
                            <TrendFill width={confPct} />
                          </TrendBar>
                        </CardContent>
                      </BundleCard>
                    );
                  })}
                </Box>

                {/* Pagination (bottom) */}
                <Box
                  sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}
                >
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    labelRowsPerPage="Rows per page:"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiTablePagination-toolbar': {
                        flexWrap: 'wrap',
                        gap: 1,
                        paddingLeft: 0,
                        paddingRight: 0,
                      },
                      '& .MuiTablePagination-actions': {
                        marginLeft: 0,
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </>
        )}

        {/* Contenuto per PDF: montato solo durante il download, overlay invisibile (opacity 0) */}
        {totalCount > 0 && (
          <Box
            sx={{
              display: showReportPreview ? 'flex' : 'none',
              position: 'fixed',
              inset: 0,
              overflow: 'auto',
              backgroundColor: 'rgba(17, 24, 39, 0.5)',
              padding: 2,
              boxSizing: 'border-box',
              zIndex: 2000,
              alignItems: 'flex-start',
              justifyContent: 'center',
              opacity: isDownloadingPdf ? 0 : 1,
              pointerEvents: isDownloadingPdf ? 'none' : 'auto',
            }}
          >
            <Box
              sx={{
                width: PDF_PAGE_WIDTH_PX,
                maxWidth: '100%',
                backgroundColor: '#ffffff',
                borderRadius: 2,
                padding: 2,
                boxShadow: '0 18px 60px rgba(2,6,23,.35)',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pdfPages.map((pageBundles, pageIdx) => (
                  <Box
                    key={`pdf-page-${pageIdx}`}
                    ref={(el: unknown) => {
                      // MUI Box espone un ref polimorfico (typed come unknown): qui sappiamo che è un <div>.
                      pdfPageRefs.current[pageIdx] =
                        el as HTMLDivElement | null;
                    }}
                    sx={{
                      width: PDF_PAGE_WIDTH_PX,
                      // Altezza fissa: se il contenuto sfora, html2canvas→jsPDF scala e tutto risulta “schiacciato”
                      height: PDF_PAGE_HEIGHT_PX,
                      overflow: 'hidden',
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      padding: 2,
                      boxSizing: 'border-box',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Frequently Bought Together
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Page {pageIdx + 1}/{pdfPages.length}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#6b7280', mb: 1.5 }}
                    >
                      Period: {dateRange.from.slice(0, 10)} →{' '}
                      {dateRange.to.slice(0, 10)} · Sort:{' '}
                      {sortBy === 'orders'
                        ? 'Orders (desc), then Affinity (desc)'
                        : 'Affinity (desc), then Orders (desc)'}{' '}
                      · Total: {totalCount}
                    </Typography>

                    {/* Stesso layout della pagina principale: BundleCard + CardContent, stessi gap */}
                    <Box
                      className="kipify-report"
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        columnGap: '24px',
                        rowGap: '24px',
                        paddingBottom: '16px',
                      }}
                    >
                      {pageBundles.map((b, idx) => {
                        const globalIndex = pageIdx * PDF_ITEMS_PER_PAGE + idx;
                        const p1 = parseProductTitle(b.p1_title);
                        const p2 = parseProductTitle(b.p2_title);
                        const confPct = (b.confidence ?? 0) * 100;
                        return (
                          <BundleCard
                            key={`pdf-${b.p1_id}-${b.p2_id}-${pageIdx}-${idx}`}
                            sx={{
                              breakInside: 'avoid',
                              height: 'auto', // evita stretching verticale nella grid PDF
                            }}
                          >
                            <CardContent
                              sx={{
                                p: 0,
                                '&:last-child': { pb: 0 },
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}
                              >
                                <BundleRank>#{globalIndex + 1}</BundleRank>
                                <BundleStats>
                                  <StatChip variant="orders">
                                    {b.together_orders} orders
                                  </StatChip>
                                  <StatChip variant="confidence">
                                    {confPct.toFixed(0)}% confidence
                                  </StatChip>
                                </BundleStats>
                              </Box>
                              <ProductRow sx={{ flex: 'unset' }}>
                                <ProductBlock>
                                  {b.p1_image ? (
                                    <PdfProductImage>
                                      <img src={b.p1_image} alt={p1.name} />
                                    </PdfProductImage>
                                  ) : (
                                    <PdfProductImage />
                                  )}
                                  <ProductName>{p1.name}</ProductName>
                                  {p1.color && (
                                    <ProductColor>{p1.color}</ProductColor>
                                  )}
                                </ProductBlock>
                                <PlusBadge>+</PlusBadge>
                                <ProductBlock>
                                  {b.p2_image ? (
                                    <PdfProductImage>
                                      <img src={b.p2_image} alt={p2.name} />
                                    </PdfProductImage>
                                  ) : (
                                    <PdfProductImage />
                                  )}
                                  <ProductName>{p2.name}</ProductName>
                                  {p2.color && (
                                    <ProductColor>{p2.color}</ProductColor>
                                  )}
                                </ProductBlock>
                              </ProductRow>
                              <TrendBar>
                                <TrendFill width={confPct} />
                              </TrendBar>
                            </CardContent>
                          </BundleCard>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default Bundles;
